//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { ReactElement, useEffect, useState } from 'react'
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
} from '@mui/material'
import DownloadIcon from '@mui/icons-material/Download'
import DeleteIcon from '@mui/icons-material/Delete'
import { DocumentInfo, listDocumentsLyt, urlForPdfFile, deletePdfLyt } from '@react/apx/AwsApx'
import { useOtChainState } from '@react/redux/store'
import { logOT } from '@shared/loggingShared'
import { RoleEnum } from '@shared/Enums'

export function LytReserveReports(): ReactElement {
  const [fileList, setFileList] = useState<DocumentInfo[]>([])
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [fileToDelete, setFileToDelete] = useState<string | null>(null)

  const {
    stageConfiguration: { stageName, lytPoolAddr, restAPIKey },
    chainConfiguration: { chainId, perimeterAddresses },
    userConnection: { roleEnum },
    cognitoUser,
  } = useOtChainState()

  const loadDocuments = async (): Promise<void> => {
    if (lytPoolAddr && stageName) {
      try {
        const fl = await listDocumentsLyt(stageName, lytPoolAddr)
        const sortedFiles = fl.sort((a, b) => {
          const dateA = new Date(a.modifiedDate)
          const dateB = new Date(b.modifiedDate)
          return dateB.getTime() - dateA.getTime()
        })
        setFileList(sortedFiles)
      } catch (e) {
        logOT.error('Error loading reserve reports:', e)
      }
    }
  }

  useEffect(() => {
    loadDocuments()
  }, [lytPoolAddr, chainId, perimeterAddresses, stageName])

  const handleDownload = async (fileName: string): Promise<void> => {
    const url = urlForPdfFile(stageName, fileName)
    const displayName = fileName.substring(fileName.lastIndexOf('/') + 1)

    try {
      const response = await fetch(url)
      const blob = await response.blob()

      const blobUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = displayName
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      logOT.error('Error downloading file:', error)
      window.open(url, '_blank')
    }
  }

  const handleDeleteClick = (fileName: string, event: React.MouseEvent): void => {
    event.stopPropagation()
    setFileToDelete(fileName)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async (): Promise<void> => {
    if (fileToDelete && lytPoolAddr && cognitoUser) {
      try {
        const displayName = fileToDelete.substring(fileToDelete.lastIndexOf('/') + 1)
        await deletePdfLyt(stageName, lytPoolAddr, displayName, restAPIKey, cognitoUser)
        await loadDocuments()
        setDeleteDialogOpen(false)
        setFileToDelete(null)
      } catch (error) {
        logOT.error('Error deleting file:', error)
        setDeleteDialogOpen(false)
        setFileToDelete(null)
      }
    }
  }

  const handleDeleteCancel = (): void => {
    setDeleteDialogOpen(false)
    setFileToDelete(null)
  }

  const canDelete =
    roleEnum === RoleEnum.POOL_ADMIN ||
    roleEnum === RoleEnum.BORROWER ||
    roleEnum === RoleEnum.ISSUER ||
    roleEnum === RoleEnum.PROTOCOL_MANAGER

  if (fileList.length === 0) {
    return (
      <Box
        sx={{
          backgroundColor: '#1a1a1a',
          padding: 4,
          borderRadius: 2,
          border: '1px solid rgba(255, 255, 255, 0.12)',
        }}
      >
        <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)', textAlign: 'center' }}>
          No reserve reports available
        </Typography>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        height: fileList.length > 9 ? '280px' : 'auto',
        overflowY: fileList.length > 9 ? 'auto' : 'visible',
        overflowX: 'hidden',
        paddingRight: fileList.length > 9 ? '8px' : '0',
        '&::-webkit-scrollbar': {
          width: '8px',
        },
        '&::-webkit-scrollbar-track': {
          backgroundColor: '#00000e',
          borderRadius: '4px',
        },
        '&::-webkit-scrollbar-thumb': {
          backgroundColor: '#d95f80',
          borderRadius: '4px',
          '&:hover': {
            backgroundColor: '#c44a73',
          },
        },
      }}
    >
      <Grid container spacing={3} sx={{ margin: 0, width: '100%' }}>
        {fileList.map((fileInfo) => {
          const fileName = fileInfo.fileName.substring(fileInfo.fileName.lastIndexOf('/') + 1)
          const displayName = fileName.replace('.pdf', '')

          return (
            <Grid item xs={12} sm={6} md={4} key={fileInfo.fileName}>
              <Card
                onClick={() => void handleDownload(fileInfo.fileName)}
                sx={{
                  backgroundColor: 'transparent',
                  border: '1px solid #d95f80',
                  borderRadius: 2,
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  height: '80px',
                  display: 'flex',
                  alignItems: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(217, 95, 128, 0.1)',
                    borderColor: '#c44a73',
                  },
                }}
              >
                <CardContent
                  sx={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    p: 2,
                    '&:last-child': {
                      pb: 2,
                    },
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, mr: 2, overflow: 'hidden' }}>
                    <Typography
                      variant="body1"
                      sx={{
                        color: '#d95f80',
                        fontWeight: 500,
                        fontSize: '14px',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: '100%',
                      }}
                      title={displayName}
                    >
                      {displayName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        fontSize: '12px',
                        mt: 0.5,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {new Date(fileInfo.modifiedDate).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                      size="small"
                      sx={{
                        color: '#d95f80',
                        flexShrink: 0,
                        '&:hover': {
                          backgroundColor: 'rgba(217, 95, 128, 0.1)',
                        },
                      }}
                    >
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                    {canDelete && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleDeleteClick(fileInfo.fileName, e)}
                        sx={{
                          color: 'rgba(255, 255, 255, 0.5)',
                          flexShrink: 0,
                          '&:hover': {
                            color: '#ff6b6b',
                            backgroundColor: 'rgba(255, 107, 107, 0.1)',
                          },
                        }}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )
        })}
      </Grid>

      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle sx={{ color: 'white', backgroundColor: '#1a1a1a' }}>Delete Reserve Report</DialogTitle>
        <DialogContent sx={{ backgroundColor: '#1a1a1a' }}>
          <Typography sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Are you sure you want to delete this reserve report? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ backgroundColor: '#1a1a1a' }}>
          <Button onClick={handleDeleteCancel} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleDeleteConfirm()}
            sx={{
              color: '#ff6b6b',
              '&:hover': {
                backgroundColor: 'rgba(255, 107, 107, 0.1)',
              },
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
